import React, { Fragment, useCallback, useEffect, useState } from 'react'
import { ApolloClient } from '@apollo/client'
import { Transition } from '@headlessui/react'
import classNames from 'classnames'
import { setCookie } from 'cookies-next'
import { When } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { Image } from '@/atoms/Image'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { AsH1, AsH2, CaptionSM, HeadingXS, ParagraphMD, ParagraphSM } from '@/atoms/Text'
import { LOGIN_STARTED_ORIGIN } from '@/constants/cookies'
import { paths } from '@/constants/paths'
import { useLanding } from '@/contexts/LandingContext'
import { FloatingCallToAction } from '@/molecules/FloatingCallToAction'
import { GiftGuildButton } from '@/molecules/PayItForwardButton/GuildGiftButton'
import { GuildPermissions } from '@/services/GuildUserService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeAnalytics } from '@/utils/analytics'
import { useLoginUrl } from '@/utils/auth-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { GuildEmailInput } from '@/views/GuildSignupView/GuildEmailInput'

const buttonClassNames = 'justify-center min-w-[121px] px-3 py-[11px] w-full sm:w-auto'

const textShadowStyle = {
  textShadow: '0px 1px 0px rgba(0,0,0,0.3)',
}

interface MainHeroSectionProps {
  client: ApolloClient
  isLoggedIn: boolean
  loading: boolean
  preview: boolean
  isGuildMember: boolean
  guildPermissions: GuildPermissions
  newVersion?: boolean
}

export const MainHeroSection: React.FC<MainHeroSectionProps> = ({
  client,
  loading,
  preview,
  isLoggedIn,
  isGuildMember,
  guildPermissions,
  newVersion = false,
}) => {
  const { t } = useTranslate('home')
  const { locale } = useLocale()
  const router = useSafeRouter()
  const { track } = useSafeAnalytics()
  const { trackLoginStarted } = useLoginUrl()
  const { guildHomeHero } = useLanding()
  const [currentSlide, setCurrentSlide] = useState(0)
  const guildHref = guildHomeHero?.homeSlidesCollection?.items[currentSlide]?.guildHref
  const href = guildHomeHero?.homeSlidesCollection?.items[currentSlide]?.href
  const heroImgPaths = {
    mobile: '/ar_2:3,g_north_east,c_fill/v1756846603/angel-studios/landing/Redesign/background_landing_new.webp',
    tablet: '/ar_16:9,g_center,c_fill/v1756846603/angel-studios/landing/Redesign/background_landing_new.webp',
    desktop: '/ar_16:9,g_north_east,c_fill/v1756846603/angel-studios/landing/Redesign/background_landing_new.webp',
    ultrawide: '/ar_21:9,g_north_east,c_fill/v1756846603/angel-studios/landing/Redesign/background_landing_new.webp',
  }

  const handleLoginClicked = useCallback(() => {
    track('Home Sign Up Clicked')
    trackLoginStarted('home-hero')
    setCookie(LOGIN_STARTED_ORIGIN, 'home')
  }, [track, trackLoginStarted])

  const handleStatsBoxClick = useCallback(() => {
    track('Home Stats Box Clicked')
    isGuildMember && guildHref ? router.push(guildHref) : href && router.push(href)
  }, [guildHref, href, isGuildMember, router, track])

  useEffect(() => {
    const interval = setInterval(() => {
      if (currentSlide === (guildHomeHero?.homeSlidesCollection?.items?.length ?? 0) - 1) {
        setCurrentSlide(0)
      } else {
        setCurrentSlide(currentSlide + 1)
      }
    }, 6500)
    return () => clearInterval(interval)
  }, [currentSlide, guildHomeHero?.homeSlidesCollection?.items?.length])

  const logoImageUrl = guildHomeHero?.homeSlidesCollection?.items[currentSlide]?.logoImageUrl

  return (
    <main className={classNames('relative h-screen min-h-[550px] w-full', { 'overflow-hidden': newVersion })}>
      {!loading && (
        <FloatingCallToAction
          client={client}
          locale={locale}
          preview={preview}
          isGuildMember={isGuildMember}
          guildPermissions={guildPermissions}
        />
      )}
      {guildHomeHero?.homeSlidesCollection?.items[currentSlide]?.desktopImageUrl &&
      guildHomeHero?.homeSlidesCollection?.items[currentSlide]?.tabletImageUrl &&
      guildHomeHero?.homeSlidesCollection?.items[currentSlide]?.mobileImageUrl ? (
        <Fragment key={currentSlide}>
          <Transition
            show
            appear
            as="div"
            enter="transition-opacity duration-1000"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-5500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <ResponsiveBackgroundImage
              className="h-screen bg-cover bg-top md:bg-right-top"
              preBackgroundStyles="linear-gradient(180deg, rgba(21, 21, 21, 0.3) 50%, #151515 100%), "
              src={guildHomeHero?.homeSlidesCollection?.items[currentSlide]?.desktopImageUrl ?? ''}
              srcUrls={{
                xs: guildHomeHero?.homeSlidesCollection?.items[currentSlide]?.mobileImageUrl ?? '',
                sm: guildHomeHero?.homeSlidesCollection?.items[currentSlide]?.mobileImageUrl ?? '',
                md: guildHomeHero?.homeSlidesCollection?.items[currentSlide]?.tabletImageUrl ?? '',
                lg: guildHomeHero?.homeSlidesCollection?.items[currentSlide]?.desktopImageUrl ?? '',
                xl: guildHomeHero?.homeSlidesCollection?.items[currentSlide]?.desktopImageUrl ?? '',
                '2xl': guildHomeHero?.homeSlidesCollection?.items[currentSlide]?.desktopImageUrl ?? '',
              }}
            />
          </Transition>
        </Fragment>
      ) : (
        <ResponsiveBackgroundImage
          className={classNames(
            newVersion ? 'h-full w-full bg-top-right aspect-w-16 aspect-h-9 absolute top-0 right-0' : 'h-screen',
          )}
          src={heroImgPaths.desktop}
          srcUrls={{
            xs: heroImgPaths.mobile,
            sm: heroImgPaths.mobile,
            md: heroImgPaths.tablet,
            lg: heroImgPaths.desktop,
            xl: heroImgPaths.desktop,
            '2xl': heroImgPaths.ultrawide,
          }}
          transforms={newVersion ? 'g_face,c_fill' : undefined}
        />
      )}
      <When condition={newVersion}>
        {/* Left -> Right Gradient */}
        <div
          className={classNames(
            'absolute hidden md:flex inset-0 left-0 top-0 h-full min-w-1/2 xl:min-w-[60%] transition-opacity',
          )}
          style={{
            background:
              'linear-gradient(90deg, rgba(21, 21, 21, 0.9) 0%, rgba(21, 21, 21, 0.7) 34.24%, rgba(21, 21, 21, 0) 69.25%)',
          }}
        />
        {/* Top -> Bottom Gradient */}
        <div
          className={classNames('absolute inset-0 left-0 top-0 min-h-1/6 w-full transition-opacity')}
          style={{
            background: 'linear-gradient(180deg, #151515 0%, rgba(21, 21, 21, 0) 30%)',
            height: '100vh',
          }}
        />
        {/* Bottom -> Top Gradient Mobile */}
        <div
          className={classNames('absolute inset-0 left-0 bottom-0 min-h-1/2 w-full transition-opacity lg:hidden')}
          style={{
            background: 'linear-gradient(0deg, #141414 28.15%, rgba(21, 21, 21, 0) 70%)',
            height: '100vh',
          }}
        />
        {/* Bottom -> Top Gradient Desktop */}
        <div
          className={classNames('absolute inset-0 left-0 bottom-0 min-h-1/2 w-full transition-opacity hidden lg:block')}
          style={{
            background: 'linear-gradient(0deg, #141414 18.15%, rgba(21, 21, 21, 0) 70%)',
            height: '100vh',
          }}
        />
      </When>
      <div
        className={classNames(
          'relative h-full flex w-full flex-col items-center md:items-start justify-center md:justify-between text-center md:text-left mt-20 se:mt-4 md:mt-0',
        )}
      >
        <div></div>
        <header
          className={classNames(
            'mx-6 flex max-w-[342px] flex-col gap-4 md:max-w-[520px] md:pl-6 lg:mt-16 lg:min-w-[700px] lg:max-w-full lg:pl-10',
            { 'z-40': newVersion },
          )}
        >
          <HeadingXS
            className="md:photon-heading-sm lg:photon-heading-md xl:photon-heading-xl 2xl:display se:photon-title-lg md:max-w-[440px] lg:max-w-[500px]"
            weight="bold"
            as={AsH1}
            style={textShadowStyle}
          >
            {guildHomeHero?.title ??
              t('joinMovementChangingEntertainmentCapitalized', 'Join the Movement Changing Entertainment.')}
          </HeadingXS>

          <ParagraphSM
            weight="medium"
            color={newVersion ? 'core-gray-400' : 'core-gray-200'}
            as={AsH2}
            className="md:photon-paragraph-md lg:photon-paragraph-xl se:photon-paragraph-xs"
          >
            {guildHomeHero?.description ??
              t(
                'startWatchingPopularShowsAndMovies',
                'Start watching popular shows and movies, curated by our fans.',
              )}{' '}
            <span className="font-bold lg:block">
              {guildHomeHero?.subtitle ??
                t('beAngelInsider', 'Be an Angel insider, helping to curate a library of shows that amplify light.')}
            </span>
          </ParagraphSM>
          <When condition={isLoggedIn}>
            <HeroButtons
              isLoggedIn={isLoggedIn}
              isGuildMember={isGuildMember}
              handleLoginClicked={handleLoginClicked}
              buttonClassNames={buttonClassNames}
              signupButtonText={t('getStarted', 'Get Started')}
            />
          </When>
          <When condition={newVersion && !isLoggedIn}>
            <ParagraphMD color="core-gray-400" className="mb-4">
              {t('enterEmailStartWatchingNow', 'Enter your email to start watching now!')}
            </ParagraphMD>
          </When>
          <When condition={!isLoggedIn}>
            <div className={classNames('w-full', newVersion ? 'items-start justify-start' : '')}>
              <GuildEmailInput
                variant={newVersion ? 'centered-oxide' : 'left-white'}
                loginOrigin="home"
                inputContainerClassName={classNames(newVersion && 'max-w-[326px]')}
                className={classNames(newVersion && 'max-w-[490px]')}
                hasUser={false}
                isCentered={false}
                isHome
              />
            </div>
          </When>
        </header>
        <section className="flex w-full flex-col items-center justify-center gap-6 md:flex-row-reverse md:justify-between md:gap-0 md:px-8 md:pb-8 lg:px-16 lg:pb-16">
          <div key={currentSlide}>
            {guildHomeHero && (
              <Transition
                show
                appear
                as="div"
                className={''}
                enter="transition-opacity duration-1000"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-5500"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div
                  className={classNames(
                    'hidden md:flex min-h-[80px] w-[200px] flex-col items-center justify-start gap-2 rounded-2xl bg-core-gray-950/70 p-4 se:p-2 se:min-h-[66px] lg:min-h-[86px]',
                    {
                      'cursor-pointer': href || (isGuildMember && guildHref),
                    },
                  )}
                  onClick={handleStatsBoxClick}
                >
                  {logoImageUrl && (
                    <div className="flex max-h-6 max-w-full items-center justify-center lg:max-h-[30px] lg:min-h-[30px]">
                      <Image
                        alt={guildHomeHero?.homeSlidesCollection?.items[currentSlide]?.statsDescription ?? ''}
                        height={24}
                        width={150}
                        className="mx-auto max-h-6 w-auto max-w-[150px] lg:max-h-[30px]"
                        src={getCloudinaryImageUrl({
                          path: logoImageUrl,
                          transforms: 'e_trim',
                        })}
                      />
                    </div>
                  )}
                  <CaptionSM className="text-pretty" color="core-gray-300">
                    <When condition={isGuildMember}>
                      {guildHomeHero?.homeSlidesCollection?.items[currentSlide]?.guildStatsDescription ??
                        guildHomeHero?.homeSlidesCollection?.items[currentSlide]?.statsDescription}
                    </When>
                    <When condition={!isGuildMember}>
                      {guildHomeHero?.homeSlidesCollection?.items[currentSlide]?.statsDescription}
                    </When>
                  </CaptionSM>
                </div>
              </Transition>
            )}
          </div>
          <ul className="flex h-full min-w-[98px] flex-row gap-1 md:items-end md:pb-2">
            {guildHomeHero?.homeSlidesCollection?.items?.map((item, index) => (
              <li
                key={`${index}${item}`}
                onClick={() => setCurrentSlide(index)}
                className="flex cursor-pointer items-center justify-center p-1"
              >
                <Transition
                  show={currentSlide === index}
                  as="div"
                  className="max-h-[10px] overflow-hidden rounded-full"
                  enter="transition ease-in-out duration-850"
                  enterFrom="-translate-x-4 scale-x-0 w-2.5 bg-core-gray-500"
                  enterTo="translate-x-0 scale-x-1 w-8 bg-white"
                >
                  <div
                    className={classNames('bg-white max-h-2.5 h-2.5 w-2.5 rounded-full ', {
                      '!w-8': currentSlide === index,
                    })}
                    key={`${item}${index}`}
                  />
                </Transition>
                {currentSlide !== index && (
                  <div
                    className={classNames('h-2.5 w-2.5 rounded-full bg-core-gray-500', {
                      'bg-white !w-8': currentSlide === index,
                    })}
                    key={`${item}${index}`}
                  />
                )}
              </li>
            ))}
          </ul>
          <div className="md:min-w-[200px]"></div>
        </section>
      </div>
    </main>
  )
}

const HeroButtons = ({
  isLoggedIn,
  isGuildMember,
  handleLoginClicked,
  buttonClassNames,
  signupButtonText,
}: {
  isLoggedIn: boolean
  isGuildMember: boolean
  handleLoginClicked: () => void
  buttonClassNames: string
  signupButtonText: string
}) => {
  const { t } = useTranslate('home')
  const { track } = useSafeAnalytics()

  return (
    <div className="mx-0 mb-12 mt-6 flex flex-row items-center justify-center gap-4 md:mx-0 md:flex-row md:justify-start se:mb-4">
      {isLoggedIn ? (
        <LinkButton
          href={paths.watch.index}
          variant="white"
          className={classNames('min-w-[175px] se:!max-h-[38px] se:!text-xs se:!leading-3 se:!mb-2', buttonClassNames)}
          onClick={() => track('Home Watch Clicked')}
        >
          <div className="relative flex w-full flex-row items-center justify-center gap-2">
            <PlayIcon color="none" className="fill-inherit" size={22} />
            {t('watchNow', 'Watch Now')}
          </div>
        </LinkButton>
      ) : (
        <LinkButton
          href={paths.signup.welcome}
          variant="white"
          className={classNames('se:!text-xs se:!leading-3 se:!max-h-[38px] se:!mb-2', buttonClassNames)}
          onClick={handleLoginClicked}
        >
          {signupButtonText}
        </LinkButton>
      )}
      {isGuildMember ? (
        <GiftGuildButton />
      ) : (
        <LinkButton
          href={paths.guild.join}
          variant="white"
          outline
          className={classNames(
            buttonClassNames,
            'se:!text-xs se:!leading-3 se:!max-h-[38px] border-0 hover:!bg-transparent se:!mb-2',
          )}
          onClick={() => track('Home Join the Guild Clicked')}
        >
          {t('joinTheGuild', 'Join the Guild')}
        </LinkButton>
      )}
    </div>
  )
}
